import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

const AdminLogin = () => {
  const [isSetupMode, setIsSetupMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checkingSetup, setCheckingSetup] = useState(true);
  const navigate = useNavigate();
  const { login, setupAdmin, checkSetup, isAuthenticated } = useAuth();

  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });

  const [setupData, setSetupData] = useState({
    email: '',
    password: '',
    full_name: ''
  });

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/admin/dashboard');
    } else {
      checkIfSetupRequired();
    }
  }, [isAuthenticated]);

  const checkIfSetupRequired = async () => {
    const result = await checkSetup();
    setIsSetupMode(result.setup_required);
    setCheckingSetup(false);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await login(loginData.email, loginData.password);

    if (result.success) {
      toast.success('Успешен вход!');
      navigate('/admin/dashboard');
    } else {
      toast.error(result.error || 'Грешен имейл или парола');
    }

    setLoading(false);
  };

  const handleSetup = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await setupAdmin(setupData.email, setupData.password, setupData.full_name);

    if (result.success) {
      toast.success('Admin профил създаден! Моля влезте.');
      setIsSetupMode(false);
      setLoginData({ email: setupData.email, password: '' });
    } else {
      toast.error(result.error || 'Грешка при създаване');
    }

    setLoading(false);
  };

  if (checkingSetup) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0B0B0B]">
        <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0B0B0B] px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img 
            src="/images/deus-logo.png" 
            alt="Deus Estate" 
            className="h-20 mx-auto mb-6"
          />
          <h1 className="text-3xl font-serif font-semibold text-[#F6F2E8] mb-2">
            {isSetupMode ? 'Първоначална настройка' : 'Admin Вход'}
          </h1>
          <p className="text-[#A9A9A9]">
            {isSetupMode ? 'Създайте вашия admin профил' : 'Влезте в админ панела'}
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-[#111111] border border-[#D4AF37]/20 rounded-xl p-8">
          {isSetupMode ? (
            <form onSubmit={handleSetup} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="full_name" className="text-[#F6F2E8]">
                  Пълно име
                </Label>
                <Input
                  id="full_name"
                  type="text"
                  required
                  value={setupData.full_name}
                  onChange={(e) => setSetupData({...setupData, full_name: e.target.value})}
                  className="bg-[#0B0B0B] border-[#D4AF37]/20 text-[#F6F2E8] focus:border-[#D4AF37]"
                  placeholder="Вашето име"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-[#F6F2E8]">
                  Имейл
                </Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={setupData.email}
                  onChange={(e) => setSetupData({...setupData, email: e.target.value})}
                  className="bg-[#0B0B0B] border-[#D4AF37]/20 text-[#F6F2E8] focus:border-[#D4AF37]"
                  placeholder="admin@deusestate.com"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-[#F6F2E8]">
                  Парола
                </Label>
                <Input
                  id="password"
                  type="password"
                  required
                  value={setupData.password}
                  onChange={(e) => setSetupData({...setupData, password: e.target.value})}
                  className="bg-[#0B0B0B] border-[#D4AF37]/20 text-[#F6F2E8] focus:border-[#D4AF37]"
                  placeholder="••••••••"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full btn-primary"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Създаване...
                  </>
                ) : (
                  'Създай Admin профил'
                )}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleLogin} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="login-email" className="text-[#F6F2E8]">
                  Имейл
                </Label>
                <Input
                  id="login-email"
                  type="email"
                  required
                  value={loginData.email}
                  onChange={(e) => setLoginData({...loginData, email: e.target.value})}
                  className="bg-[#0B0B0B] border-[#D4AF37]/20 text-[#F6F2E8] focus:border-[#D4AF37]"
                  placeholder="admin@deusestate.com"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="login-password" className="text-[#F6F2E8]">
                  Парола
                </Label>
                <Input
                  id="login-password"
                  type="password"
                  required
                  value={loginData.password}
                  onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                  className="bg-[#0B0B0B] border-[#D4AF37]/20 text-[#F6F2E8] focus:border-[#D4AF37]"
                  placeholder="••••••••"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full btn-primary"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Влизане...
                  </>
                ) : (
                  'Вход'
                )}
              </Button>
            </form>
          )}
        </div>

        {/* Footer */}
        <div className="text-center mt-6">
          <p className="text-sm text-[#A9A9A9]">
            Deus Estate Admin Panel
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
